import React, { useEffect, useState } from "react";
import api, { fileUrl, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, Eye, EyeOff } from "lucide-react";

const EMPTY = {
  name: "", address: "", city: "Alicante", postal_code: "", phone: "",
  email: "", website: "", instagram: "", picture: "", description: "",
};

export default function AdminWorkshops() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null); // null = closed, "new" = create, id = edit
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/workshops").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditing("new"); };
  const openEdit = (w) => {
    setForm({
      name: w.name || "", address: w.address || "", city: w.city || "",
      postal_code: w.postal_code || "", phone: w.phone || "", email: w.email || "",
      website: w.website || "", instagram: w.instagram || "", picture: w.picture || "",
      description: w.description || "",
    });
    setEditing(w.id);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing === "new") {
        await api.post("/admin/workshops", form);
        toast.success("Taller creado");
      } else {
        await api.put(`/admin/workshops/${editing}`, form);
        toast.success("Taller actualizado");
      }
      setEditing(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const toggle = async (id) => {
    try { await api.patch(`/admin/workshops/${id}/toggle`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("¿Eliminar taller?")) return;
    try { await api.delete(`/admin/workshops/${id}`); toast.success("Eliminado"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const inputCls = "w-full border border-neutral-300 px-3 py-2.5 outline-none focus:border-black bg-white text-sm";

  return (
    <div data-testid="admin-workshops">
      <div className="flex items-end justify-between mb-8">
        <div>
          <h1 className="font-serif text-4xl font-light tracking-tight mb-2">Talleres de enmarcación</h1>
          <p className="text-sm text-neutral-500">Talleres asociados que se muestran al cliente tras la compra, según su ciudad de envío.</p>
        </div>
        <button onClick={openNew} className="bg-black text-white px-5 py-3 overline hover:bg-neutral-800 transition-colors flex items-center gap-2" data-testid="new-workshop-btn">
          <Plus size={14} strokeWidth={1.5} /> Nuevo taller
        </button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {list.map((w) => (
          <div key={w.id} className={`bg-white border ${w.is_active ? "border-neutral-200" : "border-neutral-200 opacity-60"}`} data-testid={`workshop-${w.id}`}>
            {w.picture && (
              <div className="aspect-[4/3] bg-neutral-100 overflow-hidden">
                <img src={fileUrl(w.picture)} alt={w.name} className="w-full h-full object-cover" />
              </div>
            )}
            <div className="p-5">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h3 className="font-serif text-xl">{w.name}</h3>
                  <div className="text-xs text-neutral-500 mt-1">{w.address}, {w.city} {w.postal_code}</div>
                </div>
                {!w.is_active && <span className="overline text-xs text-neutral-500">oculto</span>}
              </div>
              {w.description && <p className="text-xs text-neutral-600 mt-3 line-clamp-2">{w.description}</p>}
              <div className="mt-3 text-xs space-y-1 text-neutral-700">
                {w.phone && <div>📞 {w.phone}</div>}
                {w.email && <div>✉ {w.email}</div>}
                {w.website && <a href={w.website} target="_blank" rel="noreferrer" className="text-neutral-900 underline block truncate">{w.website}</a>}
              </div>
              <div className="mt-4 flex gap-2 overline text-xs">
                <button onClick={() => openEdit(w)} className="border border-neutral-300 px-3 py-1.5 hover:bg-neutral-100 flex items-center gap-1" data-testid={`edit-${w.id}`}><Edit2 size={12} /> Editar</button>
                <button onClick={() => toggle(w.id)} className="border border-neutral-300 px-3 py-1.5 hover:bg-neutral-100 flex items-center gap-1" data-testid={`toggle-${w.id}`}>
                  {w.is_active ? <><EyeOff size={12} /> Ocultar</> : <><Eye size={12} /> Mostrar</>}
                </button>
                <button onClick={() => remove(w.id)} className="border border-red-200 text-red-600 px-3 py-1.5 hover:bg-red-50 flex items-center gap-1" data-testid={`delete-${w.id}`}><Trash2 size={12} /></button>
              </div>
            </div>
          </div>
        ))}
        {list.length === 0 && <div className="col-span-full text-center py-16 overline text-neutral-400">Sin talleres todavía</div>}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setEditing(null)} data-testid="workshop-modal">
          <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="bg-white max-w-2xl w-full p-8 my-8 max-h-[90vh] overflow-y-auto">
            <h2 className="font-serif text-3xl mb-6">{editing === "new" ? "Nuevo taller" : "Editar taller"}</h2>
            <div className="space-y-3">
              <input required placeholder="Nombre" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="ws-name" />
              <input required placeholder="Dirección" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className={inputCls} data-testid="ws-address" />
              <div className="grid grid-cols-2 gap-3">
                <input required placeholder="Ciudad" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className={inputCls} data-testid="ws-city" />
                <input placeholder="Código postal" value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} className={inputCls} data-testid="ws-postal" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input placeholder="Teléfono" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} data-testid="ws-phone" />
                <input type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} data-testid="ws-email" />
              </div>
              <input placeholder="Web (https://...)" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className={inputCls} data-testid="ws-website" />
              <input placeholder="Instagram (@usuario)" value={form.instagram} onChange={(e) => setForm({ ...form, instagram: e.target.value })} className={inputCls} data-testid="ws-instagram" />
              <input placeholder="URL de imagen (https://...)" value={form.picture} onChange={(e) => setForm({ ...form, picture: e.target.value })} className={inputCls} data-testid="ws-picture" />
              <textarea placeholder="Descripción breve" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={`${inputCls} h-24 resize-none`} data-testid="ws-description" />
            </div>
            <div className="mt-6 flex gap-3">
              <button disabled={saving} type="submit" className="bg-black text-white px-8 py-3 overline hover:bg-neutral-800 disabled:opacity-50" data-testid="ws-save">{saving ? "..." : "Guardar"}</button>
              <button type="button" onClick={() => setEditing(null)} className="border border-neutral-300 px-8 py-3 overline hover:bg-neutral-100" data-testid="ws-cancel">Cancelar</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
